import React, { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMe,
  useCompleteMyProfile,
  getGetMeQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserRound } from "lucide-react";

// Surface a server-provided error message (`{ error }` / `{ message }`) when one
// is available, falling back to a generic line otherwise.
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const message =
        (data as { message?: unknown }).message ??
        (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

/**
 * Full-screen profile completion for internal staff and account managers, shown
 * once after activation (the outer gate routes them here until
 * profileComplete is true). Agents/master dealers never reach this — they are
 * profile-complete by definition. The backend re-checks activation inline and
 * mirrors contact details onto the linked account-manager record.
 */
export function CompleteProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: me, isLoading } = useGetMe();
  const complete = useCompleteMyProfile();

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  // Prefill from the existing record once it loads.
  const seededRef = React.useRef(false);
  useEffect(() => {
    if (!seededRef.current && me) {
      seededRef.current = true;
      setFullName(me.fullName ?? "");
      setPhone(me.phone ?? "");
      setJobTitle(me.jobTitle ?? "");
    }
  }, [me]);

  const submit = async () => {
    try {
      await complete.mutateAsync({
        data: {
          fullName: fullName.trim(),
          phone: phone.trim(),
          jobTitle: jobTitle.trim(),
        },
      });
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast({
        title: "Profile saved",
        description: "Welcome to Mooya.",
      });
    } catch (err) {
      toast({
        title: "Could not save your profile",
        description: apiErrorMessage(err, "Please check your details and try again."),
        variant: "destructive",
      });
    }
  };

  if (isLoading || !me) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // Must be activated first; the gate enforces this, but guard directly too.
  if (me.activated !== true) {
    return <Redirect to="/activate" />;
  }
  // Already complete — nothing to do here.
  if (me.profileComplete === true) {
    return <Redirect to="/home" />;
  }

  const valid = Boolean(fullName.trim() && phone.trim() && jobTitle.trim());

  return (
    <div className="min-h-[100dvh] w-full bg-[#0B132B] text-white flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-[460px] flex flex-col">
        <header className="flex items-center gap-2 mb-10">
          <img
            src={`${import.meta.env.BASE_URL}mooya-mark.png`}
            alt="Mooya"
            className="w-8 h-8 object-contain"
          />
          <span className="font-bold text-lg tracking-tight">Mooya</span>
        </header>

        <div className="w-12 h-12 rounded-full bg-[#E6007E]/15 flex items-center justify-center mb-6">
          <UserRound className="w-6 h-6 text-[#E6007E]" />
        </div>

        <h1 className="text-3xl font-bold tracking-tight mb-3">
          Complete your profile
        </h1>
        <p className="text-slate-300 mb-8">
          Tell us a little about yourself to finish setting up your account.
        </p>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="profile-name" className="text-slate-200">
              Full name
            </Label>
            <Input
              id="profile-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              placeholder="Thandi Nkosi"
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-phone" className="text-slate-200">
              Mobile number
            </Label>
            <Input
              id="profile-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="tel"
              placeholder="082 123 4567"
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-job" className="text-slate-200">
              Job title
            </Label>
            <Input
              id="profile-job"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              autoComplete="organization-title"
              placeholder="Account Manager"
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
            />
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={!valid || complete.isPending}
          className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full h-11 mt-8"
        >
          {complete.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Save and continue"
          )}
        </Button>
      </div>
    </div>
  );
}
